/**
 * Validator factory functions.
 *
 * Each factory returns a function that takes a value and returns
 * an error message string (if invalid) or null (if valid).
 *
 * Per-step stories will add validators here (ein, zipCode, sicCode,
 * usPhone, email, abaRouting, firstOfMonth, etc.).
 */

export type ValidatorFn = (value: unknown) => string | null;

export function required(message = "This field is required"): ValidatorFn {
  return (value: unknown) => {
    if (value === null || value === undefined) return message;
    if (typeof value === "string" && value.trim() === "") return message;
    if (typeof value === "number" && isNaN(value)) return message;
    return null;
  };
}

export function fileRequired(message = "A file is required"): ValidatorFn {
  return (value: unknown) => {
    if (value === null || value === undefined) return message;
    if (typeof value === "object" && value !== null && "fileId" in value) return null;
    return message;
  };
}

// Skip validation for empty values — pair with `required()` when the field
// is both required and format-checked. Empty + non-required should pass silently.
function skipEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  return false;
}

export function ein(message = "Enter a valid EIN (XX-XXXXXXX)"): ValidatorFn {
  return (value: unknown) => {
    if (skipEmpty(value)) return null;
    return /^\d{2}-\d{7}$/.test(String(value)) ? null : message;
  };
}

export function zipCode(message = "Enter a 5-digit zip code"): ValidatorFn {
  return (value: unknown) => {
    if (skipEmpty(value)) return null;
    return /^\d{5}$/.test(String(value)) ? null : message;
  };
}

export function sicCode(message = "Enter a 3 or 4-digit SIC code"): ValidatorFn {
  return (value: unknown) => {
    if (skipEmpty(value)) return null;
    return /^\d{3,4}$/.test(String(value)) ? null : message;
  };
}

export function validSicCode(codes: Set<string>, message = "Invalid SIC"): ValidatorFn {
  return (value: unknown) => {
    if (skipEmpty(value)) return null;
    const s = String(value);
    if (!/^\d{3,4}$/.test(s)) return null; // let sicCode() handle format errors
    if (codes.size === 0) return null; // codes not loaded yet — skip validation
    return codes.has(s) ? null : message;
  };
}

export function year(message?: string): ValidatorFn {
  const currentYear = new Date().getFullYear();
  const msg = message ?? `Enter a year between 1800 and ${currentYear}`;
  return (value: unknown) => {
    if (skipEmpty(value)) return null;
    const s = String(value);
    if (!/^\d{4}$/.test(s)) return msg;
    const n = parseInt(s, 10);
    return n >= 1800 && n <= currentYear ? null : msg;
  };
}

export function minValue(min: number, message?: string): ValidatorFn {
  const msg = message ?? `Must be at least ${min}`;
  return (value: unknown) => {
    if (skipEmpty(value)) return null;
    const n = typeof value === "number" ? value : parseFloat(String(value));
    if (!Number.isFinite(n)) return msg;
    return n >= min ? null : msg;
  };
}

export function maxValue(max: number, message?: string): ValidatorFn {
  const msg = message ?? `Must be at most ${max}`;
  return (value: unknown) => {
    if (skipEmpty(value)) return null;
    const n = typeof value === "number" ? value : parseFloat(String(value));
    if (!Number.isFinite(n)) return msg;
    return n <= max ? null : msg;
  }
}

export function usPhone(message = "Enter a valid phone number"): ValidatorFn {
  return (value: unknown) => {
    if (skipEmpty(value)) return null;
    const digits = String(value).replace(/\D/g, "");
    return digits.length === 10 ? null : message;
  };
}

export function email(message = "Enter a valid email address"): ValidatorFn {
  return (value: unknown) => {
    if (skipEmpty(value)) return null;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value)) ? null : message;
  };
}

/**
 * Coverage Effective Date must be the 1st of a month and no earlier than the
 * 1st of last month. Input is the <input type="date"> YYYY-MM-DD string.
 */
export function firstOfMonth(message?: string): ValidatorFn {
  return (value: unknown) => {
    if (skipEmpty(value)) return null;
    const s = String(value);
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (!match || match[3] !== "01") {
      return message ?? "Must be the 1st of the month";
    }
    const selected = new Date(`${match[1]}-${match[2]}-01T00:00:00`);
    const now = new Date();
    const earliest = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    if (selected < earliest) {
      return message ?? "Earliest allowed date is the 1st of last month";
    }
    return null;
  };
}

export function minArrayLength(min: number, message: string): ValidatorFn {
  return (value: unknown) => {
    if (!Array.isArray(value) || value.length < min) return message;
    return null;
  };
}

/** Check if a single employee has missing required fields given current plan metadata. */
export function isEmployeeIncomplete(
  emp: { medicalCoverage?: string; medicalPlan?: string; dentalCoverage?: string; visionCoverage?: string },
  availablePlans: number, offersDental: boolean, offersVision: boolean,
): boolean {
  if (availablePlans > 1 && emp.medicalCoverage === "Enrolling" && !emp.medicalPlan) return true;
  if (offersDental && (!emp.dentalCoverage || emp.dentalCoverage === "N/A")) return true;
  if (offersVision && (!emp.visionCoverage || emp.visionCoverage === "N/A")) return true;
  return false;
}

export function employeesComplete(availablePlans: number, offersDental: boolean, offersVision: boolean): ValidatorFn {
  return (value: unknown) => {
    if (!Array.isArray(value) || value.length === 0) return null;
    const hasIncomplete = value.some((e) => isEmployeeIncomplete(e as Record<string, string>, availablePlans, offersDental, offersVision));
    if (hasIncomplete) return "Some employees have incomplete selections. Please review highlighted employees.";
    return null;
  };
}

/**
 * Per-row validators returning a field→message map for one row. Used both by
 * the aggregate step validators (which collapse them into a single error) and
 * by StepReview (which keys each field independently so individual rows can
 * highlight). Single source of truth — no duplicated regex/range checks.
 */

export interface AffiliatedGroupRow {
  name?: string;
  taxId?: string;
  employees: number | null;
  covered?: boolean | null;
}

export function validateAffiliatedGroupRow(g: AffiliatedGroupRow): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!g.name?.trim()) errors.name = "Entity Legal Name is required";
  if (!g.taxId?.trim()) errors.taxId = "Federal Tax ID is required";
  else {
    const einErr = ein()(g.taxId);
    if (einErr) errors.taxId = einErr;
  }
  if (g.employees === null) errors.employees = "No. of Eligible Employees is required";
  else if (g.employees < 0 || g.employees > 9999) errors.employees = "No. of Eligible Employees must be 0–9999";
  if (g.covered === null) errors.covered = "Covered Under Optimyl Policy is required";
  return errors;
}

export interface OwnerRow {
  firstName?: string;
  lastName?: string;
}

/**
 * Single source for the "no owners" message — the step-3 schema, StepStructure's
 * per-entity alert, and StepReview's red rows all render it (the copy had drifted
 * three ways before AB#3572).
 */
export const OWNER_REQUIRED_MESSAGE = "At least one owner is required";

/**
 * A roster row with no first name, last name, or MI is an empty scaffold row —
 * ownership-exempt org types (Nonprofit/Other) skip these instead of failing them.
 */
export function isBlankOwnerRow(o: OwnerRow & { mi?: string }): boolean {
  return !o.firstName?.trim() && !o.lastName?.trim() && !o.mi?.trim();
}

export function validateOwnerRow(o: OwnerRow): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!o.firstName?.trim()) errors.firstName = "First Name is required";
  if (!o.lastName?.trim()) errors.lastName = "Last Name is required";
  return errors;
}

export interface OwnershipAssignmentRow {
  id?: string;
  ownerId?: string;
  entityId?: string;
  ownership?: number;
  receivingW2Wages?: boolean | null;
  enrollingInBenefits?: boolean | null;
}

export function validateOwnershipAssignmentRow(a: OwnershipAssignmentRow): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!a.ownerId?.trim()) errors.ownerId = "Owner is required";
  if (a.ownership === undefined || a.ownership === null || a.ownership <= 0) errors.ownership = "Ownership % is required";
  if (a.receivingW2Wages === null || a.receivingW2Wages === undefined) errors.receivingW2Wages = "Please select Yes or No";
  if (a.enrollingInBenefits === null || a.enrollingInBenefits === undefined) errors.enrollingInBenefits = "Please select Yes or No";
  return errors;
}

export function affiliatedGroupsComplete(): ValidatorFn {
  return (value: unknown) => {
    if (!Array.isArray(value) || value.length === 0) return null;
    for (let i = 0; i < value.length; i++) {
      const rowErrors = validateAffiliatedGroupRow(value[i] as AffiliatedGroupRow);
      const firstKey = Object.keys(rowErrors)[0];
      if (firstKey) return `Entity ${i + 1}: ${rowErrors[firstKey]}`;
    }
    return null;
  };
}

/**
 * Validates each owner roster row for completeness. With `ignoreEmptyRows`, rows that
 * are COMPLETELY blank (no first, last, or MI) are skipped — used for ownership-exempt
 * org types (Nonprofit/Other), where the form seeds one empty scaffold row that would
 * otherwise block a legitimately owner-less submission. Partially-filled rows always
 * validate.
 */
export function ownersComplete(ignoreEmptyRows = false): ValidatorFn {
  return (value: unknown) => {
    if (!Array.isArray(value)) return null;
    for (let i = 0; i < value.length; i++) {
      const row = value[i] as OwnerRow & { mi?: string };
      if (ignoreEmptyRows && isBlankOwnerRow(row)) continue;
      const rowErrors = validateOwnerRow(row);
      const firstKey = Object.keys(rowErrors)[0];
      if (firstKey) return `Owner ${i + 1}: ${rowErrors[firstKey]}`;
    }
    return null;
  };
}

/**
 * Sums ownership % per required entity and returns any entity whose rows don't
 * total exactly 100%. Also flags rows with duplicate owners within a single
 * entity, and any assignment referencing an entity that no longer exists.
 * Returns the first error found so the aggregate step message has a
 * deterministic pick — StepReview computes per-row errors separately.
 */
export function ownershipAssignmentsComplete(
  requiredEntityIds: string[],
  entityLabelById: (id: string) => string,
  enforceTotals = true,
): ValidatorFn {
  return (value: unknown) => {
    if (!Array.isArray(value)) return null;
    const assignments = value as OwnershipAssignmentRow[];

    // Per-row completeness
    for (let i = 0; i < assignments.length; i++) {
      const rowErrors = validateOwnershipAssignmentRow(assignments[i]);
      const firstKey = Object.keys(rowErrors)[0];
      if (firstKey) {
        const label = entityLabelById(assignments[i].entityId ?? "");
        return `${label}: ${rowErrors[firstKey]}`;
      }
    }

    // Check every required entity, plus any entity that actually has rows — a
    // voluntarily-provided breakdown (e.g. an ownership-exempt Nonprofit/Other group
    // that chose to enter owners anyway) must still be coherent: no duplicate owners
    // and a 100% total. Only the "at least one owner" minimum is limited to the
    // required entities.
    const entityIdsToCheck = [...requiredEntityIds];
    for (const a of assignments) {
      const id = a.entityId ?? "";
      if (id && !entityIdsToCheck.includes(id)) entityIdsToCheck.push(id);
    }

    for (const entityId of entityIdsToCheck) {
      const rows = assignments.filter((a) => a.entityId === entityId);
      if (rows.length === 0) {
        if (requiredEntityIds.includes(entityId)) {
          return `${entityLabelById(entityId)}: ${OWNER_REQUIRED_MESSAGE}`;
        }
        continue;
      }
      // Duplicate owner within a single entity
      const seen = new Set<string>();
      for (const r of rows) {
        const key = r.ownerId ?? "";
        if (seen.has(key)) {
          return `${entityLabelById(entityId)}: an owner is listed twice`;
        }
        seen.add(key);
      }
      // Ownership-exempt org types (Nonprofit/Other) skip the 100% rule entirely —
      // a partial voluntary breakdown is acceptable there.
      if (enforceTotals) {
        const total = rows.reduce((sum, r) => sum + (Number(r.ownership) || 0), 0);
        const rounded = Math.round(total * 100) / 100;
        if (rounded !== 100) {
          return `${entityLabelById(entityId)}: ownership must total 100% (currently ${rounded}%)`;
        }
      }
    }

    return null;
  };
}
